import React, { FC, useCallback, useEffect, useState } from 'react';
import { Field, Form } from 'react-final-form';
import {
  Button,
  FormControl,
  LocalLoading,
  TextAreaAdapter,
  TextFieldAdapter,
  validations,
} from '../../../shared/components';
import { useFetch, useUser } from '../../../shared';
import { useTranslation } from 'react-i18next';
import apiRoutes from '../../../../config/api/routes';
import { Theme, styled } from '@material-ui/core';

type ContactFormProps = {
  onSubmit: (values: ContactFormData) => void;
};

export type ContactToken = {
  token: string;
};

export type ContactFormData = {
  title: string;
  message: string;
};

const ButtonWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const UserFeedback = styled(LocalLoading)<Theme>(({ theme }) => ({
  marginLeft: theme.spacing(2),
}));

const Wrapper = styled('div')<Theme>(({ theme }) => ({
  margin: theme.spacing(4, 0),
}));

// TODO - remove this component as soon as hubspot form is validated
export const ContactForm: FC<ContactFormProps> = ({ onSubmit }) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const [formData, setFormData] = useState<ContactFormData>();
  const [submitting, setSubmitting] = useState(false);

  const [contactMailResponse, contactMail] = useFetch(apiRoutes.contactMail(), {
    method: 'POST',
  });

  const handleFormSubmit = useCallback(
    (values) => {
      const data = new FormData();

      if (values.file !== undefined) {
        data.append('file', values.file[0]);
      }
      data.append('email_address', values.email);
      data.append('name', `${user.firstName} ${user.name}`);
      data.append('title', values.title);
      data.append('message', values.message);

      contactMail({
        body: data,
        multiPartFormData: true,
      });

      setFormData(values);
      setSubmitting(true);
    },
    [user, contactMail]
  );

  useEffect(() => {
    if (contactMailResponse.success && formData) {
      onSubmit(formData);
      setFormData(undefined);
    }
  }, [onSubmit, formData, contactMailResponse]);

  return (
    <Wrapper>
      <Form
        onSubmit={handleFormSubmit}
        render={({ handleSubmit, valid }) => (
          <form onSubmit={handleSubmit}>
            <FormControl id={'title'} label={t('contact.form.title')} required>
              <Field
                name={'title'}
                type={'text'}
                component={TextFieldAdapter}
                validate={validations(t).required}
              />
            </FormControl>
            <FormControl id={'email'} label={t('shared.forms.email')} required>
              <Field
                name="email"
                component={TextFieldAdapter}
                validate={validations(t).isEmail()}
                type="email"
              />
            </FormControl>
            <FormControl id={'file'} label={t('contact.form.file')}>
              <Field<FileList> name="file">
                {({ input: { value, onChange, ...input } }) => (
                  <input {...input} type="file" onChange={({ target }) => onChange(target.files)} />
                )}
              </Field>
            </FormControl>
            <FormControl id={'message'} label={t('contact.form.message')} required>
              <Field
                name={'message'}
                type={'text'}
                component={TextAreaAdapter}
                validate={validations(t).required}
              />
            </FormControl>
            <ButtonWrapper>
              <Button type={'submit'} disabled={submitting || !valid}>
                {t('contact.form.submit')}
              </Button>
              {submitting && <UserFeedback message={t('contact.form.pleaseWait')} />}
            </ButtonWrapper>
          </form>
        )}
      />
    </Wrapper>
  );
};
